import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ProtectedRoute } from '../components/auth/index.js';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { useState } from 'react';
import {
  useKpiSummary,
  useKpiTrend,
  useKpiWeeklyTrend,
  useIpointTrend,
  useAceMonthlyTrend,
  useBudgetFiscalYearStatus,
} from '../hooks/index.js';
import { ErrorState } from '../components/feedback/ErrorState/index.js';
import {
  KpiDetailModal,
  BugDetectionDaysModal,
} from '../components/features/KpiDetailModal/index.js';
import {
  KpiChatPanel,
  KpiSummaryTable,
  KpiTrendCharts,
  IpointMonthlyTrendChart,
  IpointMonthDetailModal,
  WeeklyPointTrendChart,
  WeeklyPointDetailModal,
  AceMonthlyTrendChart,
  PointProgressSection,
  SreOpenBugTrendSection,
  BudgetFiscalYearWarning,
  computeDefaultWeeklyDateRange,
  DEFAULT_DEVELOP_POINT_VIEW,
  type DevelopPointView,
} from '../components/features/kpi/index.js';
import { PmoView } from '../components/features/pmo/index.js';
import { TEAM_OPTIONS } from '@cortex/domain-product-innovation-lab-kpi-team-filter';
import { getTeamDisplayName, isTeamRowShown } from './-kpi-team-filter.js';
import { useBugDetectionModal } from '../hooks/useBugDetectionModal.js';
import { useBugDetectionDistribution } from '../hooks/useBugDetectionDistribution.js';
import { useKpiDetailModal } from '../hooks/useKpiDetailModal.js';
import type { IpointTrendDateBasis, KpiTeam } from '../types/api.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business KPIダッシュボードページ。チーム別バグ率/リリースPT/開発PTの4種トレンドチャートと週次pt集計チャート、ACE月次トレンド、pt進捗比較、i-point月次推移チャート、サマリーテーブル（KPI一覧。行クリックでそのチームのバグ明細モーダルを開く）、ドリルダウンモーダル、AIチャットパネルを統合表示
 * @graph-connects useKpiSummary [calls] KPIサマリーフック呼び出し
 * @graph-connects useKpiTrend [calls] KPIトレンドフック呼び出し
 * @graph-connects useKpiWeeklyTrend [calls] 週次pt集計フック呼び出し
 * @graph-connects useAceMonthlyTrend [calls] ACE月次トレンドフック呼び出し
 * @graph-connects useIpointTrend [calls] i-point月次推移フック呼び出し
 * @graph-connects useBudgetFiscalYearStatus [calls] fibo予算FY切替状況フック呼び出し
 * @graph-connects KpiDetailModal [calls] KPI詳細ドリルダウンモーダル表示
 * @graph-connects KpiSummaryTable [calls] KPI一覧テーブル表示
 * @graph-connects KpiChatPanel [calls] KPIチャットパネル表示
 * @graph-connects WeeklyPointTrendChart [calls] 週次pt集計チャートカード表示
 * @graph-connects AceMonthlyTrendChart [calls] ACE月次トレンドチャートカード表示
 * @graph-connects PointProgressSection [calls] pt進捗比較セクション表示
 * @graph-connects IpointMonthlyTrendChart [calls] i-point月次推移チャートカード表示
 * @graph-connects IpointMonthDetailModal [calls] i-point月次内訳モーダル表示
 * @graph-connects BudgetFiscalYearWarning [calls] FY切替警告バナー表示
 * @graph-connects Select [calls] セレクトコンポーネント描画
 * @graph-connects ErrorState [calls] エラー表示描画
 * @graph-connects getTeamDisplayName [calls] チーム表示名変換
 * @graph-connects isTeamRowShown [calls] サマリー表のチーム行の表示判定
 * @graph-connects PmoView [calls] PMO KPIビュー（プロセス/デリバリー切替）表示
 * @graph-connects SreOpenBugTrendSection [calls] SREビューの未完了バグ件数の増減表示
 */
export const Route = createFileRoute('/kpi')({
  component: KpiRoute,
});

/**
 * ルートガード。内部メンバーのみアクセス可能（サイドバー表示と同一の SSOT で判定）。
 * @graph-connects ProtectedRoute [calls] ページアクセス可否を判定しガードする
 */
function KpiRoute() {
  return (
    <ProtectedRoute pageKey="kpi">
      <KpiPage />
    </ProtectedRoute>
  );
}

/**
 * 上部セレクトの選択肢。既存のチームスコープ (`KpiTeam` / `all`) に加えて、
 * チームではない専用ビュー `pmo`（PMO フェーズリードタイム）を持つ。
 * 共有ドメイン型 `KpiTeam` は広げず、この画面のローカル型に閉じる。
 */
type KpiView = KpiTeam | 'all' | 'pmo';

/**
 * @graph-connects useKpiSummary [calls] KPIサマリーデータ取得
 * @graph-connects useKpiTrend [calls] KPIトレンドデータ取得
 * @graph-connects useKpiWeeklyTrend [calls] 週次pt集計データ取得
 * @graph-connects useKpiPointProgress [calls] pt進捗比較データ取得
 * @graph-connects useIpointTrend [calls] i-point月次推移データ取得
 * @graph-connects useBudgetFiscalYearStatus [calls] fibo予算FY切替状況データ取得
 * @graph-connects KpiDetailModal [calls] KPI詳細モーダル表示
 * @graph-connects KpiSummaryTable [calls] KPI一覧テーブル表示（行クリックでバグ明細モーダルを開く）
 * @graph-connects KpiChatPanel [calls] KPIチャットパネル表示
 * @graph-connects WeeklyPointTrendChart [calls] 週次pt集計チャート表示
 * @graph-connects WeeklyPointDetailModal [calls] 週次pt 担当者別内訳モーダル表示
 * @graph-connects PointProgressSection [calls] pt進捗比較セクション表示
 * @graph-connects computeDefaultWeeklyDateRange [calls] 週次pt集計デフォルト範囲生成
 * @graph-connects IpointMonthlyTrendChart [calls] i-point月次推移チャート表示
 * @graph-connects IpointMonthDetailModal [calls] i-point月次内訳モーダル表示
 * @graph-connects BudgetFiscalYearWarning [calls] fibo予算FY切替警告バナー表示
 * @graph-connects PmoView [calls] PMO KPIビュー（プロセス/デリバリー切替）表示
 * @graph-connects SreOpenBugTrendSection [calls] SREビューの未完了バグ件数の増減表示
 */
function KpiPage() {
  const { t } = useTranslation();

  // チームオプションは 5 画面共通カタログ（TEAM_OPTIONS）を正本とする。SRE は含まれない。
  // SRE は KPI 画面でだけ選べるようにし、PMO はチームスコープではなくチーム横断の専用ビューなので、
  // どちらもここでだけ末尾に足す。
  const teamOptions: { value: KpiView; label: string }[] = [
    ...TEAM_OPTIONS.map((o) => ({
      value: o.value as KpiView,
      label: o.value === 'all' ? t('kpi.scope.all') : o.label,
    })),
    { value: 'sre', label: getTeamDisplayName('sre') },
    { value: 'pmo', label: 'PMO' },
  ];

  const periodOptions = [
    { value: '7', label: t('time.last7Days') },
    { value: '14', label: t('time.last14Days') },
    { value: '30', label: t('time.last30Days') },
    { value: '90', label: t('time.last90Days') },
    { value: '180', label: t('time.last180Days') },
    { value: '365', label: t('time.last1Year') },
  ];
  const [selectedView, setSelectedView] = useState<KpiView>('all');
  // PMO ビューはチームスコープを持たないため、既存の KPI 系フック / フィルタから見ると
  // 「全体」と同じ undefined になる（既存の型・呼び出しは一切変えない）
  const isPmoView = selectedView === 'pmo';
  // SRE ビューはチーム共通の KPI ではなく SRE 専用の表示に切り替える
  const isSreView = selectedView === 'sre';
  const showTeamKpiContent = !isPmoView && !isSreView;
  const selectedTeam: KpiTeam | undefined =
    selectedView === 'all' || isPmoView ? undefined : selectedView;
  const [selectedDays, setSelectedDays] = useState('90');
  const [selectedIpointMonths, setSelectedIpointMonths] = useState('12');
  const [selectedIpointDateBasis, setSelectedIpointDateBasis] =
    useState<IpointTrendDateBasis>('releasedAt');
  const [selectedAceMonths, setSelectedAceMonths] = useState('12');

  // 週次pt集計のデフォルト範囲: 8週前の月曜 〜 4週後の日曜（過去 + 一部将来）
  // override が null の間は render 毎にデフォルトを再計算するため、日付を
  // またいでアプリを開きっぱなしにしても初期範囲が陳腐化しない
  const [weeklyDateOverride, setWeeklyDateOverride] = useState<{
    start: string;
    end: string;
  } | null>(null);
  const weeklyDateRange = weeklyDateOverride ?? computeDefaultWeeklyDateRange();

  // 開発pt の口径（全部 / 要件定義抜き / 内訳）。開発pt (MTD) カードと週次pt集計カードで共有する
  const [developPointView, setDevelopPointView] = useState<DevelopPointView>(
    DEFAULT_DEVELOP_POINT_VIEW,
  );

  // 詳細モーダル用の状態（特定日付のドリルダウン）
  const kpiDetail = useKpiDetailModal(() => {
    const team = selectedTeam ?? 'all';
    return { team, teamDisplayName: getTeamDisplayName(team) };
  });

  // バグ検知日数平均チャートのドリルダウンモーダル用の状態
  const bugDetection = useBugDetectionModal(() => {
    const team = selectedTeam ?? 'all';
    return { team, teamDisplayName: getTeamDisplayName(team) };
  });

  // i-point月次内訳モーダル用の状態
  const [ipointMonthModal, setIpointMonthModal] = useState<{
    open: boolean;
    month: string;
    totalIpoint: number;
    bugCount: number;
  }>({
    open: false,
    month: '',
    totalIpoint: 0,
    bugCount: 0,
  });

  const closeIpointMonthModal = () => {
    setIpointMonthModal((prev) => ({ ...prev, open: false }));
  };

  // 週次pt 担当者別ドリルダウンモーダル用の状態
  const [weeklyDetailModal, setWeeklyDetailModal] = useState<{
    open: boolean;
    weekStart: string;
    weekEnd: string;
  }>({
    open: false,
    weekStart: '',
    weekEnd: '',
  });

  const closeWeeklyDetailModal = () => {
    setWeeklyDetailModal((prev) => ({ ...prev, open: false }));
  };

  // 全チームのサマリーを取得（クライアントサイドでフィルタ）
  const { data, isLoading, error, refetch } = useKpiSummary();

  // トレンドデータを取得
  const { data: trendData, isLoading: isTrendLoading } = useKpiTrend({
    team: selectedTeam ?? 'all',
    days: Number(selectedDays),
  });

  // バグ検知日数分布チャート用: トレンドと同じ窓の案件起因バグを個別スポットとして取得
  const bugDetectionPoints = useBugDetectionDistribution(
    selectedTeam ?? 'all',
    Number(selectedDays),
  );

  // i-point月次推移データを取得
  const { data: ipointTrendData, isLoading: isIpointTrendLoading } = useIpointTrend({
    team: selectedTeam,
    months: Number(selectedIpointMonths),
    dateBasis: selectedIpointDateBasis,
  });

  // 週次pt集計データを取得
  const { data: weeklyTrendData, isLoading: isWeeklyTrendLoading } = useKpiWeeklyTrend({
    team: selectedTeam ?? 'all',
    startDate: weeklyDateRange.start,
    endDate: weeklyDateRange.end,
  });

  // 月次トレンドデータを取得（上部 team フィルタ連動）
  const { data: aceMonthlyTrendData, isLoading: isAceMonthlyTrendLoading } = useAceMonthlyTrend({
    team: selectedTeam ?? 'all',
    months: Number(selectedAceMonths),
  });

  // fibo予算 FY 切替状況を取得（severity が warning/critical なら下部に警告バナーを表示）
  //   デバッグ用：URL ?now= / ?mockFiscalYears= で判定をシミュレートできる
  //   例: ?now=2026-06-25&mockFiscalYears=26 → warning（FY26存在 + FY27 切替14日前で未作成）
  //       ?now=2026-07-15&mockFiscalYears=26 → critical（FY27突入後もFY27シート未作成）
  const debugParams =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const debugNow = debugParams?.get('now') ?? undefined;
  const debugMockFiscalYears = debugParams?.get('mockFiscalYears') ?? undefined;
  const { data: budgetFiscalYearStatus } = useBudgetFiscalYearStatus({
    now: debugNow,
    mockFiscalYears: debugMockFiscalYears,
  });

  // 選択中の期間に合わせたチャート説明文
  const trendDescription =
    selectedDays === '365'
      ? t('kpi.trendDescriptionYear')
      : t('kpi.trendDescriptionDays', { days: selectedDays });

  const filteredData = data?.data.filter((row) => isTeamRowShown(row.team, selectedTeam));

  // KPI一覧テーブルの集計基準日（API が返す日付、無ければ先頭行の日付）
  const summaryDate = data?.date ?? filteredData?.[0]?.date;

  // KPI一覧テーブルの行クリック → その行のチームのバグ詳細ドリルダウンを開く。
  // 行の指標は「集計基準日時点の過去90日」なので periodType は '90d' 固定。
  // チャート経由と違いチームは画面上部のフィルタではなくクリックされた行で決まるため
  // openModalForTeam を使う（全体表示では行ごとにチームが異なる）。
  const openRowDetail = (team: KpiTeam) => {
    if (!summaryDate) return;
    kpiDetail.openModalForTeam({
      team,
      teamDisplayName: getTeamDisplayName(team),
      date: summaryDate,
      periodType: '90d',
      chartType: 'bugRate',
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="animate-content-reveal">
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{t('kpi.title')}</h1>
          {!isSreView && (
            <p className="mt-1 text-[var(--color-text-secondary)]">{t('kpi.description')}</p>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-3 animate-content-reveal">
          <Select
            options={teamOptions}
            value={selectedView}
            onChange={(e) => {
              setSelectedView(e.target.value as KpiView);
            }}
            size="sm"
          />
          {/* 期間セレクトはチームKPIのトレンド用。PMOビューは自前の集計期間セレクトを持つ */}
          {showTeamKpiContent && (
            <Select
              options={periodOptions}
              value={selectedDays}
              onChange={(e) => {
                setSelectedDays(e.target.value);
              }}
              size="sm"
            />
          )}
        </div>
      </div>

      {isPmoView && <PmoView />}

      {isSreView && <SreOpenBugTrendSection />}

      {showTeamKpiContent && (
        <>
          {/* エラー発生時 */}
          {error && (
            <ErrorState
              title={t('feedback.errorTitle')}
              message={t('feedback.errorMessage')}
              onRetry={() => {
                void refetch();
              }}
            />
          )}

          <KpiTrendCharts
            trendData={trendData}
            isTrendLoading={isTrendLoading}
            trendDescription={trendDescription}
            onOpenDetail={kpiDetail.openModal}
            onOpenBugDetectionDetail={bugDetection.openModal}
            bugDetectionPoints={bugDetectionPoints}
            team={selectedTeam ?? 'all'}
            developPointView={developPointView}
            onDevelopPointViewChange={setDevelopPointView}
          />

          {/* 週次pt集計チャート */}
          <WeeklyPointTrendChart
            data={weeklyTrendData?.data ?? []}
            isLoading={isWeeklyTrendLoading}
            startDate={weeklyDateRange.start}
            endDate={weeklyDateRange.end}
            team={selectedTeam ?? 'all'}
            view={developPointView}
            onViewChange={setDevelopPointView}
            onDateRangeChange={(start, end) => {
              setWeeklyDateOverride({ start, end });
            }}
            onWeekClick={(weekStart, weekEnd) => {
              setWeeklyDetailModal({ open: true, weekStart, weekEnd });
            }}
          />

          {/* 月次トレンド（Target / Actual / Person-month、上部 team フィルタ連動） */}
          <AceMonthlyTrendChart
            data={aceMonthlyTrendData?.data ?? []}
            isLoading={isAceMonthlyTrendLoading}
            selectedMonths={selectedAceMonths}
            onMonthsChange={setSelectedAceMonths}
            team={selectedTeam ?? 'all'}
          />

          {/* pt進捗比較（進捗目標pt vs 進捗実績pt、上部 team フィルタ連動 + 期間フィルタ） */}
          <PointProgressSection team={selectedTeam ?? 'all'} />

          {/* fibo予算 FY 切替警告（severity = warning/critical のときのみ表示） */}
          <BudgetFiscalYearWarning status={budgetFiscalYearStatus} />

          {/* i-point 月次推移チャート */}
          <IpointMonthlyTrendChart
            data={ipointTrendData?.data ?? []}
            isLoading={isIpointTrendLoading}
            selectedMonths={selectedIpointMonths}
            onMonthsChange={setSelectedIpointMonths}
            selectedDateBasis={selectedIpointDateBasis}
            onDateBasisChange={setSelectedIpointDateBasis}
            onMonthClick={(month) => {
              const point = ipointTrendData?.data.find((p) => p.month === month);
              setIpointMonthModal({
                open: true,
                month,
                totalIpoint: point?.totalIpoint ?? 0,
                bugCount: point?.bugCount ?? 0,
              });
            }}
          />

          {/* KPI Table（KPI一覧。行クリックでそのチームのバグ明細モーダルを開く） */}
          <KpiSummaryTable
            rows={filteredData}
            summaryDate={summaryDate}
            isLoading={isLoading}
            hasError={Boolean(error)}
            getTeamLabel={getTeamDisplayName}
            onRowClick={openRowDetail}
          />

          {/* 詳細モーダル */}
          <KpiDetailModal
            open={kpiDetail.modal.open}
            onClose={kpiDetail.closeModal}
            team={kpiDetail.modal.team}
            teamDisplayName={kpiDetail.modal.teamDisplayName}
            date={kpiDetail.modal.date}
            periodType={kpiDetail.modal.periodType}
            chartType={kpiDetail.modal.chartType}
          />

          {/* バグ検知日数平均 詳細モーダル */}
          <BugDetectionDaysModal
            open={bugDetection.modal.open}
            onClose={bugDetection.closeModal}
            team={bugDetection.modal.team}
            teamDisplayName={bugDetection.modal.teamDisplayName}
            date={bugDetection.modal.date}
          />

          {/* i-point月次内訳モーダル */}
          <IpointMonthDetailModal
            open={ipointMonthModal.open}
            onClose={closeIpointMonthModal}
            month={ipointMonthModal.month}
            dateBasis={selectedIpointDateBasis}
            team={selectedTeam}
            teamDisplayName={getTeamDisplayName(selectedTeam ?? 'all')}
            totalIpoint={ipointMonthModal.totalIpoint}
            bugCount={ipointMonthModal.bugCount}
          />

          {/* 週次pt 担当者別内訳モーダル */}
          <WeeklyPointDetailModal
            open={weeklyDetailModal.open}
            onClose={closeWeeklyDetailModal}
            weekStart={weeklyDetailModal.weekStart}
            weekEnd={weeklyDetailModal.weekEnd}
            team={selectedTeam}
            teamDisplayName={getTeamDisplayName(selectedTeam ?? 'all')}
          />

          {/* チャットパネル */}
          <KpiChatPanel selectedTeam={selectedTeam ?? 'all'} selectedDays={Number(selectedDays)} />
        </>
      )}
    </div>
  );
}
